import { Directive, Inject } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { ActivatedRoute, NavigationExtras } from '@angular/router';
import {
  ActivityBodyInterface,
  ActivityInterface,
} from '@sport-activities/sdk';
import { Observable } from 'rxjs';

import { FormGroupValueInterface } from '~/component/form/interfaces/form-group-value.interface';
import { RoutingInterface } from '~/core/services/navigation/interfaces/routing.interface';
import { NavigationService } from '~/core/services/navigation/navigation.service';
import { TitleService } from '~/core/services/title/title.service';
import { AbstractOrganizerSharedFormPage } from '~/organizer/shared/components/shared/form-wrapper/classes/abstract-form-page';
import { NAVIGATION_ROUTING } from '~/organizer/shared/components/shared/navigation-buttons/navigation-buttons.component';
import { OrganizerSharedActivityService } from '~/organizer/shared/services/activity/activity.service';
import { OrganizerSharedOrganizationService } from '~/organizer/shared/services/organization/organization.service';

/**
 * Keep this decorator to allow constructor's params injection in child's classes
 */
@Directive()
export abstract class AbstractOrganizerActivityCreationFormPageDirective extends AbstractOrganizerSharedFormPage<
  ActivityBodyInterface,
  ActivityInterface
> {
  constructor(
    protected readonly activatedRoute: ActivatedRoute,
    protected readonly activityService: OrganizerSharedActivityService,
    protected readonly formBuilder: FormBuilder,
    protected readonly navigationService: NavigationService,
    protected readonly organizationService: OrganizerSharedOrganizationService,
    @Inject(NAVIGATION_ROUTING)
    protected readonly navigationRouting: RoutingInterface[],
    protected readonly titleService: TitleService
  ) {
    super(
      activatedRoute,
      formBuilder,
      navigationService,
      navigationRouting,
      titleService
    );
  }

  protected buildActivityData(
    formGroupValue: FormGroupValueInterface
  ): Partial<ActivityInterface> {
    return this.buildDataBeforeSubmit(
      formGroupValue
    ) as Partial<ActivityInterface>;
  }

  protected redirectAfterSubmit(
    navigationExtras: NavigationExtras = {}
  ): Promise<boolean> {
    const queryParameters = this.activatedRoute.snapshot.queryParams || {};
    const activity = this.activityService.activity;

    navigationExtras.queryParams = {
      ...navigationExtras.queryParams,
      ...queryParameters,
    };

    if (activity?.sku) {
      navigationExtras.queryParams = {
        ...navigationExtras.queryParams,
        activityId: activity.sku,
      };
    }

    return super.redirectAfterSubmit(navigationExtras);
  }

  protected redirectIfNoDataChanges(
    data: Partial<ActivityBodyInterface>
  ): false | Promise<boolean> {
    const activity = this.activityService.activity;

    return super.redirectIfNoDataChanges(data, activity);
  }

  protected setActivityData(formGroupValue: FormGroupValueInterface): void {
    const activity = this.activityService.activity;
    const activityWithNewValues = this.buildActivityData(formGroupValue);

    for (const attributeName of Object.keys(activityWithNewValues)) {
      activity[attributeName] = activityWithNewValues[attributeName];
    }
  }

  public submit(
    formGroupValue: FormGroupValueInterface,
    navigationExtras: NavigationExtras = {}
  ): void {
    const data = this.buildDataBeforeSubmit(
      formGroupValue
    ) as Partial<ActivityBodyInterface>;

    let activityHttpRequest$: Observable<number>;

    if (this.activityService.hasActivity()) {
      if (this.redirectIfNoDataChanges(data) !== false) {
        return;
      }

      activityHttpRequest$ = this.activityService.patchActivity(data);
    } else {
      activityHttpRequest$ = this.activityService.postActivityOnOrganization(
        data,
        this.organizationService.organization
      );
    }

    activityHttpRequest$.subscribe({
      next: () => {
        this.setActivityData(formGroupValue);

        return this.redirectAfterSubmit(navigationExtras);
      },
      error: () => this.error$.next(true),
    });
  }
}
